import React, { FC, useState, useRef, useEffect } from "react";

export interface OptionType {
  label: string;
  value: string;
}

export interface SelectMultipleProps {
  options: OptionType[];
  className?: string;
  sizeClass?: string;
  placeholder?: string;
  name: string;
  onChange: (selectedOptions: OptionType[]) => void;
}

/**
 * Tag/chip multi-select used in the recruitment form (spoken languages).
 *
 * The control box GROWS with its content (min-height, flex-wrap) instead of
 * a fixed height — otherwise wrapped chips overflow and collide with the
 * field below. Chips use the accessible green (green-ada + white) and the
 * dropdown marks already-selected options.
 */
const SelectMultiple: FC<SelectMultipleProps> = ({
  options,
  className = "",
  sizeClass = "",
  placeholder = "Select options...",
  name,
  onChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleOption = (option: OptionType) => {
    setSelectedOptions((prev) => {
      const next = prev.find((o) => o.value === option.value)
        ? prev.filter((o) => o.value !== option.value)
        : [...prev, option];
      onChange?.(next);
      return next;
    });
  };

  const removeChip = (option: OptionType) => {
    setSelectedOptions((prev) => {
      const next = prev.filter((o) => o.value !== option.value);
      onChange(next);
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSelected = (v: string) => selectedOptions.some((o) => o.value === v);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Control box — grows with the chips (no fixed height) */}
      <div
        className={`flex flex-wrap items-center gap-2 cursor-pointer ${sizeClass}`}
        style={{
          minHeight: 46,
          padding: "8px 38px 8px 12px",
          borderRadius: 14,
          border: "1.5px solid var(--g-line, #E6EAE5)",
          background: "white",
        }}
        onClick={() => setIsOpen((o) => !o)}
        role="button"
        tabIndex={0}
      >
        {selectedOptions.length === 0 && (
          <span style={{ fontSize: 13, color: "var(--g-ink-500)" }}>{placeholder}</span>
        )}
        {selectedOptions.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center"
            style={{
              gap: 6,
              background: "var(--g-green-ada, #1F7A45)",
              color: "white",
              fontSize: 12.5,
              fontWeight: 600,
              padding: "4px 5px 4px 12px",
              borderRadius: 999,
            }}
          >
            {option.label}
            <button
              type="button"
              aria-label={`Remove ${option.label}`}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(option);
              }}
              className="grid place-items-center"
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "rgba(255,255,255,.22)",
                color: "white",
                border: 0,
                cursor: "pointer",
                lineHeight: 1,
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </span>
        ))}

        {/* Chevron */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--g-ink-500)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: `translateY(-50%) rotate(${isOpen ? 180 : 0}deg)`,
            transition: "transform .15s ease",
            pointerEvents: "none",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul
          className="absolute z-20 w-full overflow-auto"
          style={{
            marginTop: 6,
            background: "white",
            border: "1px solid var(--g-line, #E6EAE5)",
            borderRadius: 14,
            maxHeight: 240,
            boxShadow: "var(--g-shadow-md, 0 18px 40px rgba(0,0,0,.16))",
            padding: 6,
            listStyle: "none",
          }}
        >
          {options.map((option) => {
            const sel = isSelected(option.value);
            return (
              <li
                key={option.value}
                onClick={() => toggleOption(option)}
                className={`flex items-center justify-between cursor-pointer ${
                  sel ? "" : "hover:bg-neutral-100"
                }`}
                style={{
                  fontSize: 13,
                  padding: "9px 12px",
                  borderRadius: 10,
                  color: sel ? "var(--g-green-ink, #0E5A2C)" : "var(--g-ink-900, #1a1a1a)",
                  fontWeight: sel ? 700 : 500,
                  ...(sel ? { background: "#E8F8EE" } : {}),
                }}
              >
                {option.label}
                {sel && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--g-green-ada, #1F7A45)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Hidden select for form submission */}
      <select
        name={name}
        multiple
        value={selectedOptions.map((o) => o.value)}
        onChange={() => {}}
        className="hidden"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default SelectMultiple;
